import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
} from '@tanstack/react-query';
import { itemsApi } from '../api';
import { transformApiItem, transformApiItems } from '../utils/data-transforms';
import type { ApiItem, ItemsResponse, ItemsQuery, Item } from '../api/types';
import * as _ from 'es-toolkit';
import { toast } from 'sonner';

// Query keys
export const queryKeys = {
  items: ['items'] as const,
  item: (id: string | number) => ['item', String(id)] as const,
  adminItems: ['admin-items'] as const,
} as const;

export interface UpdateItemData {
  title?: string;
  handle?: string;
  description?: string;
  brandId?: number;
  image?: string;
  price_1?: string;
  price_2?: string;
  status?: string;
  available?: boolean;
  tags?: string[];
  data?: any;
}

// Hook to fetch items with optional filtering and pagination
export function useItems(query?: ItemsQuery, options?: Partial<UseInfiniteQueryOptions>) {
  return useInfiniteQuery({
    queryKey: [...queryKeys.items, query],
    queryFn: async (x) => {
      const response: ItemsResponse = await itemsApi.getItems(
        _.merge({ nextCursor: x.pageParam }, query || {})
      );
      return {
        ...response,
        items: transformApiItems(response.items),
      };
    },
    staleTime: 1000 * 10,
    getNextPageParam: (lastPage) => lastPage.pagination.nextCursor,
    initialPageParam: '',
    // @ts-ignore
    ...(options as any),
  });
}

// Hook to fetch a single item by ID
export function useItem(id: string) {
  return useQuery({
    queryKey: queryKeys.item(id),
    queryFn: async (): Promise<Item> => {
      const apiItem: ApiItem = await itemsApi.getItem(id);
      return transformApiItem(apiItem);
    },
    enabled: !!id, // Only run query if ID is provided
    staleTime: 1000 * 10, // 10 minutes for single item
  });
}

// Hook to create an item (for future admin functionality)
export function useCreateItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<ApiItem>) => {
      const newItem: ApiItem = await itemsApi.createItem(data);
      return transformApiItem(newItem);
    },
    onSuccess: () => {
      // Invalidate items query to refetch list
      queryClient.invalidateQueries({ queryKey: queryKeys.items });
    },
  });
}

// Hook to update an existing item
export function useUpdateItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateItemData; item?: any }) =>
      itemsApi.updateItem(id, data),
    onMutate: async ({ id, data, item: itemProp }) => {
      // Try to get item from cache, fallback to prop
      let item = queryClient.getQueryData(queryKeys.item(id)) as any;
      if (!item) {
        item = itemProp;
      }

      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.items });
      await queryClient.cancelQueries({ queryKey: queryKeys.adminItems });
      await queryClient.cancelQueries({ queryKey: queryKeys.item(id) });

      // Snapshot previous values
      const previousItemQueries = queryClient.getQueriesData({ queryKey: queryKeys.items });
      const previousAdminQueries = queryClient.getQueriesData({ queryKey: queryKeys.adminItems });
      const previousItem = queryClient.getQueryData(queryKeys.item(id));

      // Create updated item object
      const updatedItem = {
        ...item,
        ...data,
        updatedAt: new Date().toISOString(),
      };

      // Optimistically update single item cache
      queryClient.setQueryData(queryKeys.item(id), updatedItem);

      // Optimistically update all items queries
      const updateQueryData = (old: any) => {
        if (!old) return old;

        // Handle infinite query structure
        if (old.pages) {
          return {
            ...old,
            pages: old.pages.map((page: any) => ({
              ...page,
              items: page.items.map((i: any) => (i.id === id ? updatedItem : i)),
            })),
          };
        }

        // Handle regular query structure
        if (old.items) {
          return {
            ...old,
            items: old.items.map((i: any) => (i.id === id ? updatedItem : i)),
          };
        }

        return old;
      };

      // Update both regular items queries and admin-items queries
      queryClient.setQueriesData({ queryKey: queryKeys.items }, updateQueryData);
      queryClient.setQueriesData({ queryKey: queryKeys.adminItems }, updateQueryData);

      return { previousItemQueries, previousAdminQueries, previousItem };
    },
    onError: (_error: any, { id }, context) => {
      // Rollback on error
      if (context?.previousItemQueries) {
        context.previousItemQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      if (context?.previousAdminQueries) {
        context.previousAdminQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      if (context?.previousItem) {
        queryClient.setQueryData(queryKeys.item(id), context.previousItem);
      }
      const errorData = _error.response?.data?.error;
      toast.error(errorData?.message || 'Failed to update item');
    },
    onSuccess: () => {
      toast.success('Item updated successfully');
    },
  });
}

// Hook to bulk update items (admin only)
export function useBulkUpdateItems() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      itemIds,
      updates,
    }: {
      itemIds: number[];
      updates: { active?: boolean; status?: string };
    }) => {
      return itemsApi.bulkUpdateItems(itemIds, updates);
    },
    onMutate: async ({ itemIds, updates }) => {
      // Cancel any outgoing refetches for both regular and admin queries
      await queryClient.cancelQueries({ queryKey: queryKeys.items });
      await queryClient.cancelQueries({ queryKey: queryKeys.adminItems });

      // Snapshot the previous values
      const previousItemQueries = queryClient.getQueriesData({ queryKey: queryKeys.items });
      const previousAdminQueries = queryClient.getQueriesData({ queryKey: queryKeys.adminItems });

      // Optimistically update all matching queries
      const updateQueryData = (old: any) => {
        if (!old) return old;

        // Handle infinite query structure
        if (old.pages) {
          return {
            ...old,
            pages: old.pages.map((page: any) => ({
              ...page,
              items: page.items.map((item: any) => {
                if (itemIds.includes(Number(item.id))) {
                  return {
                    ...item,
                    status: updates.status ?? item.status,
                  };
                }
                return item;
              }),
            })),
          };
        }

        // Handle regular query structure
        if (old.items) {
          return {
            ...old,
            items: old.items.map((item: any) => {
              if (itemIds.includes(Number(item.id))) {
                return {
                  ...item,
                  status: updates.status ?? item.status,
                };
              }
              return item;
            }),
          };
        }

        return old;
      };

      // Update both regular items queries and admin-items queries
      queryClient.setQueriesData({ queryKey: queryKeys.items }, updateQueryData);
      queryClient.setQueriesData({ queryKey: queryKeys.adminItems }, updateQueryData);

      return { previousItemQueries, previousAdminQueries };
    },
    onError: (_err, _variables, context) => {
      // Rollback to previous state on error
      if (context?.previousItemQueries) {
        context.previousItemQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      if (context?.previousAdminQueries) {
        context.previousAdminQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: queryKeys.items });
      queryClient.invalidateQueries({ queryKey: queryKeys.adminItems });
    },
  });
}
